import { accountsInTableOrder } from '../AccountsTable/accountLines'
import { accountFilterOf } from '../model/activityView'
import { shortInstitutionName } from '../model/institutionName'
import { monthOf } from '../model/months'
import type {
  AccountAsOf,
  NotCounted,
  PortfolioOverview,
} from '../model/portfolioOverview'
import { accountsAsOf } from '../model/portfolioOverview'
import type {
  AccountTimeline,
  ActivityQuery,
  DateWindow,
  MonthKey,
} from '../model/types'
import type {
  HoldingRow,
  PortfolioAccount,
  PortfolioPeriod,
  RealizedRow,
  RowContext,
  UnvestedAwardRow,
  YearToDateRow,
} from '../types'
import { snapshotTotals } from '../usePortfolioDashboard'
import { accountNotCounted } from './accountHistory'

// One account in view, with the statement that ends in the As-of month.
export interface AccountSection {
  account: AccountAsOf
  timeline: AccountTimeline
  // Null unless the account has a statement for the month.
  period: PortfolioPeriod | null
  holdings: HoldingRow[]
  awards: UnvestedAwardRow[]
  yearToDate: YearToDateRow[]
}

export interface DetailScope {
  month: MonthKey
  asOfDate: string
  // Null under All accounts.
  chosen: AccountSection | null
  sections: AccountSection[]
  // The Activity and Realized gains lists start from this query.
  baseQuery: ActivityQuery
  realized: RealizedRow[]
  // The months with no statement that the money column above leaves out.
  notCounted: NotCounted[]
}

const contextOf = (
  account: AccountAsOf,
  period: PortfolioPeriod,
): RowContext => ({
  account: account.accountId,
  accountLabel: account.name,
  accountNumber: account.last4,
  accountType: account.accountType,
  period: period.period,
  periodLabel: period.label,
})

const sectionOf = (
  account: AccountAsOf,
  timeline: AccountTimeline,
  folded: PortfolioAccount | undefined,
): AccountSection => {
  const segmentId = account.statement?.segmentId
  const period =
    segmentId == null
      ? null
      : (folded?.periods.find(
          (candidate) => candidate.segmentId === segmentId,
        ) ?? null)
  if (!period) {
    return {
      account,
      timeline,
      period,
      holdings: [],
      awards: [],
      yearToDate: [],
    }
  }
  const context = contextOf(account, period)
  return {
    account,
    timeline,
    period,
    holdings: period.holdings.map((holding) => ({
      ...holding,
      ...context,
      gainLoss: snapshotTotals([holding]).unrealized,
    })),
    awards: period.unvestedAwards.map((award) => ({ ...award, ...context })),
    yearToDate: period.yearToDate.map((figure) => ({ ...figure, ...context })),
  }
}

// Rows before the first statement's start only exist because that statement printed them, so the
// list keeps them until there is an earlier value to measure from.
const listRange = (
  window: DateWindow | null,
  hasChange: boolean,
  asOfDate: string,
) => ({ from: hasChange ? window?.from : undefined, to: asOfDate })

const isInRange = (
  period: PortfolioPeriod,
  range: { from?: string; to: string },
) =>
  period.endDate != null &&
  period.endDate <= range.to &&
  (range.from == null || period.endDate >= range.from)

const realizedIn = (
  sections: AccountSection[],
  foldedById: Map<string, PortfolioAccount>,
  range: { from?: string; to: string },
): RealizedRow[] =>
  sections.flatMap(({ account }) =>
    (foldedById.get(account.accountId)?.periods ?? [])
      .filter((period) => isInRange(period, range))
      .flatMap((period) =>
        period.realizedGains.map((gain) => ({
          ...gain,
          ...contextOf(account, period),
        })),
      ),
  )

const buildScope = (
  overview: PortfolioOverview,
  folded: PortfolioAccount[],
  month: MonthKey,
  selectedAccountId: string | null,
): DetailScope => {
  const asOf = accountsAsOf(overview, month)
  const foldedById = new Map(folded.map((account) => [account.id, account]))
  const timelineById = new Map(
    overview.timelines.map((timeline) => [timeline.id, timeline]),
  )
  const sections = accountsInTableOrder(asOf.accounts)
    .filter((account) =>
      selectedAccountId == null
        ? account.status !== 'before_first_statement'
        : account.accountId === selectedAccountId,
    )
    .flatMap((account) => {
      const timeline = timelineById.get(account.accountId)
      return timeline
        ? [sectionOf(account, timeline, foldedById.get(account.accountId))]
        : []
    })
  const chosen = selectedAccountId == null ? null : (sections[0] ?? null)
  const window = chosen ? chosen.account.money.window : asOf.money.window
  const hasChange = chosen ? chosen.account.change != null : asOf.change != null
  const range = listRange(window, hasChange, asOf.asOfDate)
  return {
    month,
    asOfDate: asOf.asOfDate,
    chosen,
    sections,
    baseQuery: {
      accountIds: selectedAccountId == null ? null : [selectedAccountId],
      dateRange: range,
    },
    realized: realizedIn(sections, foldedById, range),
    notCounted: chosen
      ? accountNotCounted(chosen.timeline, month, window)
      : asOf.money.notCounted,
  }
}

const scopesByOverview = new WeakMap<
  PortfolioOverview,
  WeakMap<PortfolioAccount[], Map<string, DetailScope>>
>()

// Same overview, statements, month and account give back the same scope, so a re-render does no work.
export const detailScopeFor = (
  overview: PortfolioOverview,
  folded: PortfolioAccount[],
  month: MonthKey,
  selectedAccountId: string | null,
): DetailScope => {
  const byFolded =
    scopesByOverview.get(overview) ??
    new WeakMap<PortfolioAccount[], Map<string, DetailScope>>()
  scopesByOverview.set(overview, byFolded)
  const scopes = byFolded.get(folded) ?? new Map<string, DetailScope>()
  byFolded.set(folded, scopes)
  const key = `${month}|${selectedAccountId ?? ''}`
  const cached = scopes.get(key)
  if (cached) return cached
  const scope = buildScope(overview, folded, month, selectedAccountId)
  scopes.set(key, scope)
  return scope
}

// Account names for the Account column and filter. Null when only one account is in view.
export const accountNamesOf = (
  scope: DetailScope,
): Map<string, string> | null =>
  scope.sections.length > 1
    ? new Map(
        scope.sections.map((section) => [
          section.account.accountId,
          section.account.name,
        ]),
      )
    : null

// The institution and the last 4 apart, so a list can cut a long name and still show the last 4.
export interface AccountLabel {
  institution: string
  last4: string
}

// Null when only one account is in view, since the lists then drop the Account column.
export const accountLabelsOf = (
  scope: DetailScope,
): Map<string, AccountLabel> | null =>
  scope.sections.length > 1
    ? new Map(
        scope.sections.map(({ account }) => [
          account.accountId,
          {
            institution: shortInstitutionName(account.institution),
            last4: account.last4,
          },
        ]),
      )
    : null

// The scope's not-counted months that fall inside a list's dates, for the accounts it shows.
export const notCountedWithin = (
  notCounted: NotCounted[],
  span: DateWindow | null,
  accountIds: string[] | null,
): NotCounted[] => {
  if (!span) return []
  const first = monthOf(span.from)
  const last = monthOf(span.to)
  const inView = accountFilterOf(accountIds)
  return notCounted.filter(
    (entry) =>
      entry.month >= first && entry.month <= last && inView(entry.accountId),
  )
}
